<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-75"
    menu-class="dropdown-menu-media dropdown-menu-right"
    left
  >
    <template #button-content>
      <feather-icon
        badge-classes="bg-danger"
        class="text-body"
        icon="UsersIcon"
        size="21"
        v-b-tooltip.hover.button.viewport="`${$t('user_group_selection_option.title')}`"
      />
    </template>

    <li class="dropdown-menu-header" @click.stop @mousedown.stop @mouseup.stop>
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          <span class="align-middle ml-50">{{
            $t("user_group_selection_option.title")
          }}</span>
        </h4>

        <div>
          <b-badge pill variant="light-primary">
            {{ badgeCount }} {{ $t("user_group_selection_option.groups") }}
          </b-badge>
        </div>
      </div>
    </li>

    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <div class="px-1 py-1" @click.stop @mousedown.stop @mouseup.stop>
        <b-media class="mb-1">
          <template #aside>
            <b-avatar size="32">
              <span class="d-flex align-items-center">
                <feather-icon icon="UserIcon" size="16" />
              </span>
            </b-avatar>
          </template>

          <p class="media-heading mb-50">
            <span class="font-weight-bolder">
              Grupo actual:
              <small class="notification-text">{{ currentGroupLabel }}</small>
            </span>
          </p>
        </b-media>

        <div v-if="isEditing" @click.stop @mousedown.stop @mouseup.stop>
          <p v-if="!optionGroups.length" class="media-heading mb-50">
            <span class="font-weight-bolder">
              {{ $t("user_group_selection_option.without_groups") }}
            </span>
          </p>
          <div v-else @click.stop @mousedown.stop @mouseup.stop>
            <label class="text-capitalize">{{
              $t("user_group_selection_option.groups")
            }}</label>
            <v-select
              v-model="itemData.groupId"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="optionGroups"
              class="mb-1"
              :placeholder="$t('user_group_selection_option.select')"
              :reduce="(val) => val.value"
            />
          </div>
        </div>
      </div>
    </vue-perfect-scrollbar>

    <li class="dropdown-menu-footer p-1" @click.stop @mousedown.stop @mouseup.stop>
      <div v-if="!isEditing">
        <b-button variant="primary" block @click.stop="isEditing = true">
          {{ $t("user_group_selection_option.change") }}
        </b-button>
      </div>

      <div v-else class="d-flex">
        <b-button
          variant="primary"
          block
          class="mr-50"
          :disabled="isSaving"
          @click.stop="saveChange"
        >
          {{ $t("actions.save") }}
        </b-button>
        <b-button
          variant="outline-secondary"
          block
          :disabled="isSaving"
          @click.stop="isEditing = false"
        >
          {{ $t("actions.cancel") }}
        </b-button>
      </div>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import { BNavItemDropdown, BBadge, BButton, BMedia, BAvatar } from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { computed, ref, onMounted, watch } from "@vue/composition-api";
import store from "@/store";
import vSelect from "vue-select";
import axios from "@/libs/axios";
import { decryptData } from "@/utils/decryptUserDataUtils";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { getUserData } from "@/auth/utils";
import usePassport from "@/auth/passport/usePassport";
import { initialAbility } from "@/libs/acl/config";
import ability from '@/libs/acl/ability';
import { encryptData } from "@/utils/encryptUserDataUtils";



const initItemData = {
  groupId: null,
};

export default {
  components: {
    BNavItemDropdown,
    VuePerfectScrollbar,
    BBadge,
    BButton,
    BMedia,
    BAvatar,
    vSelect,
  },
  props: {
    routeName: {
      type: String,
      default: "user_user_group_selection_option",
    },
  },
  setup() {
    const toast = useToast();

    const itemData = ref(initItemData);

    const isSaving = ref(false);
    const isEditing = ref(false);
    const userGroups = ref([]);
    const optionGroups = ref([]);

    const getUserGroups = () => {
      axios.get("v1/user-group-selection-option").then((response) => {
        const groupSelectionOptions = response.data.data.group_selection_options;

        userGroups.value = response.data.data.user_groups;

        optionGroups.value = groupSelectionOptions.map((option) => ({
          value: option.id,
          label: option.name,
        }));
      });
    };

    onMounted(() => {
      getUserGroups();
    });

    const badgeCount = computed(() => optionGroups.value?.length || 0);

    const currentGroupLabel = computed(() =>
      userGroups.value?.length ? userGroups.value[0].name : "N/A"
    );

    const logout = () => {//old logout
      // Remove userData from localStorage
      localStorage.removeItem(usePassport.passportConfig.storageTokenKeyName);
      localStorage.removeItem(usePassport.passportConfig.storageRefreshTokenKeyName);
      window.Echo.disconnect();
      window.abortController.abort();
      window.abortController = new AbortController();

      // Remove userData from localStorage
      localStorage.removeItem("userData");

      localStorage.removeItem("additionalTokenImage");

      // clear all data in vuex
      for (const [key, value] of Object.entries(store._actions)) {
        if (key.endsWith("/clear")) store.dispatch(key);
      }

      // Reset ability
      ability.update(initialAbility);

      reLogIn();
      //document.location.reload();

      // Redirect to login page
      // this.$router.push({ name: "auth-login" });
    }

    const reLogIn = async() => {
        try {
          const response = await axios.get("/v1/users/data?with[]=external_users");

          if (response.data.success) {
            const { data } = response.data;
            const userData = data;
            userData.ability = [];
            if (userData.video_wall) {
              store
              .dispatch("video_wall/online", userData)
              .then((response) => {
                console.log(response);
              })
              .catch((response) => console.log(response));
            }

          if (userData.is_master_admin) {
            userData.ability.push({
            action: "manage",
            subject: "all",
            });
          } else {
            userData.ability = data.abilities;
            userData.ability.push({
            action: "read",
            subject: "Auth",
             });

            userData.ability.push({
            action: "read",
            subject: "Home",
            });
          }

          // Update the encrypted userData in localStorage
          const userDataEncrypted = encryptData(userData);
          localStorage.setItem("userData", userDataEncrypted);
           window.location.reload();

          }
      } catch (err) {
          console.error("Error Recargando:", err);
          return;
      }

    }

    const saveChange = () => {
      isSaving.value = true;

      axios
        .post("v1/user-group-selection-option/" + itemData.value.groupId)
        .then((resp) => {
          const response = resp.data;
          if (response.success) {
            isEditing.value = false;

            getUserGroups();

            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                text: 'Inicia sesión nuevamente para ver los cambios',
                icon: "CheckIcon",
                variant: "success",
              },
            });

            setTimeout(() => {
              reLogIn();
              //logout();
            }, 1000);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          console.log({
            response,
          });
          if (response.response.status == 422) {
            let string = ``;
            Object.entries(response.response.data.data).forEach((entry) => {
              const [key, value] = entry;
              string += `${key} - ${value}<br>`;
            });
            toast(
              {
                component: ToastificationContent,
                props: {
                  title: `${response.response.data.message}`,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                  text: string,
                },
              },
              {
                timeout: 10000,
              }
            );
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.response.data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .finally(() => {
          isSaving.value = false;
        });
    };

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    return {
      perfectScrollbarSettings,
      badgeCount,
      optionGroups,
      itemData,
      saveChange,
      userGroups,
      isEditing,
      isSaving,
      currentGroupLabel,
    };
  },
};
</script>

